import styled from '@emotion/styled';
import { filterNullish } from '@modernhealth/filter-nullish';
import {
    HeadingText40,
    ComponentTextSmall,
    BodyTextMedium,
    HeadingText30,
    pxToRem,
    Icon,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from 'src/shared/components/Collapse';
import { PathwayTopicFragment, PathwayTopicGoalFragment } from '../documents/fragments.generated';

export interface PathwaysGoalsProps {
    pathwayTopics: PathwayTopicFragment[];
}

export const PathwayGoals: React.FC<PathwaysGoalsProps> = ({ pathwayTopics }) => {
    if (pathwayTopics.length === 0) {
        return null;
    }

    return (
        <GoalsWrapper>
            {pathwayTopics.map((topic, index) => {
                return <Topic key={topic.title} topic={topic} sessionNumber={index + 1} />;
            })}
        </GoalsWrapper>
    );
};

export const TopicGoal = ({ goal }: { goal: PathwayTopicGoalFragment }) => {
    return (
        <ListItem>
            <Icon name="checkmark" size={18} color="blue55" />
            <BodyTextMedium marginLeft={5} fontColor="gray85">
                {goal.title}
            </BodyTextMedium>
        </ListItem>
    );
};

const Topic: React.FC<{ topic: PathwayTopicFragment; sessionNumber: number }> = ({
    topic,
    sessionNumber,
}) => {
    const { t } = useTranslation();
    const [visible, setVisible] = React.useState(sessionNumber === 1);

    const goals = useMemo(() => filterNullish(topic.goals), [topic.goals]);

    return (
        <GoalContainer key={topic.title}>
            <StyledTopicHeader
                onClick={() => {
                    setVisible(!visible);
                }}
            >
                <div>
                    <HeadingText40>
                        {t('pathways:pathway_detail.session.topic_title', {
                            sessionNumber,
                            topicTitle: topic.title,
                        })}
                    </HeadingText40>
                    <CenteredContent>
                        <Icon name="clock" />
                        <ComponentTextSmall marginLeft={5}>
                            {t('pathways:shared.minutes', { minutes: topic?.duration })}
                        </ComponentTextSmall>
                    </CenteredContent>
                </div>
                <div>
                    <Icon name={visible ? 'chevron-up' : 'chevron-down'} size={20} color="gray85" />
                </div>
            </StyledTopicHeader>
            <Collapse visible={visible} noAnimationDelay>
                <BodyTextMedium marginTop={20} marginBottom={20} fontColor="gray85">
                    {topic.description}
                </BodyTextMedium>

                <HeadingText30>
                    {t('pathways:components.pathway_goals.session.what_you_will_work_on')}:
                </HeadingText30>
                <Ul>
                    {goals.map(goal => (
                        <TopicGoal key={goal.title} goal={goal} />
                    ))}
                </Ul>
            </Collapse>
        </GoalContainer>
    );
};

const GoalsWrapper = styled.div({
    width: '100%',
    marginTop: pxToRem(28),
});

const CenteredContent = styled.div({
    display: 'flex',
    alignItems: 'center',
});

const Ul = styled.ul({
    padding: 0,
});

const ListItem = styled.li({
    display: 'flex',
    alignItems: 'center',
    padding: pxToRem(5, 0),
});

const GoalContainer = styled.div({
    border: `1px solid ${colors.gray10}`,
    borderRadius: '8px',
    padding: pxToRem(32),
    marginBottom: pxToRem(20),
});

const StyledTopicHeader = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer',
});
